import { createContext, lazy, Suspense, useContext, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlertCircle,
  IconClock,
  IconCornerLeftUp,
  IconDownload,
  IconFile,
  IconFolder,
  IconGitBranch,
  IconHome,
  IconLock,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Tooltip } from '@/components/ui/tooltip';
import { cn, formatBytes } from '@/lib/utils';
import { AssetPreview } from '../dam/AssetPreview.js';
import { AssetBudgetBadge } from '../dam/AssetBudgetBadge.js';
import { DependencyPanel } from '../dam/DependencyPanel.js';
import { FacetSidebar } from '../dam/FacetSidebar.js';
import { RevisionDiff } from '../dam/diff/RevisionDiff.js';
import { TagChips } from '../dam/TagChips.js';
import { ThumbnailCard } from '../dam/ThumbnailCard.js';
import { api, type DepotDir, type HistoryEntry, type LockEntry } from '../api.js';
import { DAM_PREVIEWABLE_EXTS, extOf, basename } from '../lib/fileKinds.js';
import { relativeTime } from '../lib/format.js';

// CodeView statically imports react-syntax-highlighter with 18
// language modules. Lazy-loading means texture / mesh / data-asset
// viewers don't pay that bundle cost — only the .ts / .json / .ini
// text-file path triggers the chunk fetch.
const CodeView = lazy(() =>
  import('../CodeView.js').then((m) => ({ default: m.CodeView })),
);

// Search-input grammar mirrors GitHub Issues / Sourcegraph:
//   tag:<name>         must have this tag
//   tag:<a>,<b>        must have at least one of a or b
//   tag:<a> tag:<b>    must have both a AND b
//   -tag:<name>        must NOT have this tag
//   class:<X>          asset class filter (UE PascalCase)
//   prop:<term>        FTS over preview.json properties
const BULK_MAX_FILES = 200;

interface SelectionApi {
  selected: ReadonlySet<string>;
  toggle: (path: string) => void;
  selectMany: (paths: string[]) => void;
  clear: () => void;
}

const SelectionContext = createContext<SelectionApi | null>(null);

function useSelection(): SelectionApi {
  const ctx = useContext(SelectionContext);
  if (!ctx) {
    return { selected: new Set(), toggle: () => {}, selectMany: () => {}, clear: () => {} };
  }
  return ctx;
}

const CLASS_PIECE_RE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

function useFileHref(): (depotPath: string) => { pathname: string; search: string } {
  const location = useLocation();
  return (depotPath: string) => ({
    pathname: location.pathname,
    search: `?file=${encodeURIComponent(depotPath)}`,
  });
}

function toggleSearchToken(
  input: string,
  kind: 'tag' | 'class' | 'budget' | 'brokenrefs',
  name: string,
): string {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const present = new RegExp(`(?:^|\\s)${kind}:${escaped}(?=\\s|$)`).test(input);
  let out = input;
  // budget is single-select (over XOR large): clear any existing level
  // first so clicking 'over' replaces 'large' rather than ANDing them.
  if (kind === 'budget') {
    out = out.replace(/(?:^|\s)budget:\S+(?=\s|$)/g, ' ').trim().replace(/\s+/g, ' ');
  }
  if (present) {
    // Toggling the active value off. For budget the level is already
    // stripped above; for the others, remove this specific token.
    if (kind === 'budget') return out;
    const re = new RegExp(`(?:^|\\s)${kind}:${escaped}(?=\\s|$)`, 'g');
    return out.replace(re, ' ').trim().replace(/\s+/g, ' ');
  }
  const token = `${kind}:${name}`;
  return out ? `${out} ${token}` : token;
}

const TAG_PIECE_RE = /^[a-z0-9][a-z0-9_.-]{0,63}$/;

interface ParsedSearch {
  q: string;
  include: string[][];
  exclude: string[];
  includeClass: string[];
  excludeClass: string[];
  propQuery: string;
  // D-5 facets: budget level (over/large) and broken-reference toggle.
  budget: 'over' | 'large' | null;
  brokenRefs: boolean;
}

function parseSearchInput(raw: string): ParsedSearch {
  const include: string[][] = [];
  const exclude: string[] = [];
  const includeClass: string[] = [];
  const excludeClass: string[] = [];
  const propTerms: string[] = [];
  let budget: 'over' | 'large' | null = null;
  let brokenRefs = false;
  const tokenRe =
    /(?:^|\s)(-?)(tag|class|prop|budget|brokenrefs):([A-Za-z0-9][A-Za-z0-9_.,-]{0,256})(?=\s|$)/gi;
  let qParts: string[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(raw)) !== null) {
    qParts.push(raw.slice(lastIndex, m.index));
    const isNeg = m[1] === '-';
    const qualifier = m[2]!.toLowerCase();
    const rawGroup = m[3]!.split(',').map((s) => s.trim());
    if (qualifier === 'tag') {
      const group = rawGroup
        .map((s) => s.toLowerCase())
        .filter((s) => TAG_PIECE_RE.test(s));
      if (group.length > 0) {
        if (isNeg) exclude.push(...group);
        else include.push(group);
      }
    } else if (qualifier === 'class') {
      const group = rawGroup.filter((s) => CLASS_PIECE_RE.test(s));
      if (group.length > 0) {
        if (isNeg) excludeClass.push(...group);
        else includeClass.push(...group);
      }
    } else if (qualifier === 'budget') {
      const v = rawGroup[0]?.toLowerCase();
      if (v === 'over' || v === 'large') budget = v;
    } else if (qualifier === 'brokenrefs') {
      const v = rawGroup[0]?.toLowerCase();
      brokenRefs = v === '1' || v === 'true' || v === 'yes';
    } else {
      for (const term of rawGroup) if (term) propTerms.push(term);
    }
    lastIndex = tokenRe.lastIndex;
  }
  qParts.push(raw.slice(lastIndex));
  const q = qParts.join(' ').trim().replace(/\s+/g, ' ');
  return { q, include, exclude, includeClass, excludeClass, propQuery: propTerms.join(' '), budget, brokenRefs };
}

type ViewMode = 'list' | 'grid';
// Key is versioned: list has always been the default for a fresh
// browser, but the old key persisted whatever you last clicked, so
// anyone who had ever chosen grid stayed on grid forever. Bumping to
// .v2 retires those stored values once, so everyone lands on the
// GitHub-style list view; picking grid again persists under the new key.
const VIEW_MODE_STORAGE_KEY = 'theia.depot.viewMode.v2';
const DEPOT_SORT_STORAGE_KEY = 'theia.depot.sort';

// ============================================================================
// DepotPage — root
// ============================================================================

export function DepotPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const sub = location.pathname.replace(/^\/depot\/?/, '').replace(/\/$/, '');
  const depotPath = sub ? `//${sub}` : '//';

  // Heuristic: last segment contains a dot → treat as file.
  const lastSlash = depotPath.lastIndexOf('/');
  const lastSegment = depotPath.slice(lastSlash + 1);
  const looksLikeFile = lastSegment.length > 0 && lastSegment.includes('.');

  // Modal mode for `?file=<depot-path>` overlays.
  const searchParams = new URLSearchParams(location.search);
  const modalFile = searchParams.get('file');

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-4">
      <div
        // Sticky breadcrumb strip flush to the AppShell header.
        className="sticky top-0 z-10 -mx-4 border-b border-neutral-800 bg-background px-4 py-2 md:-mx-6 md:px-6"
      >
        <DepotBreadcrumbs depotPath={depotPath} />
      </div>

      {looksLikeFile ? (
        <FileView depotPath={depotPath} />
      ) : (
        <DirectoryView depotPath={depotPath} />
      )}

      <Dialog
        open={modalFile !== null}
        onOpenChange={(o) => {
          if (!o) navigate(location.pathname, { replace: true });
        }}
      >
        <DialogContent
          // Wide + tall so 3D viewers and code views breathe.
          // aria-describedby={undefined} silences Radix's "missing
          // Description" warning — the file content IS the
          // description; a separate text blurb would be noise.
          aria-describedby={undefined}
          className="max-h-[92vh] max-w-[95vw] overflow-auto"
        >
          {modalFile && (
            <>
              <DialogTitle className="font-mono">
                {basename(modalFile)}
              </DialogTitle>
              <FileView depotPath={modalFile} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DepotBreadcrumbs({ depotPath }: { depotPath: string }) {
  const parts = depotPath === '//' ? [] : depotPath.replace(/^\/\//, '').split('/');
  const segments = [
    { label: 'depot', to: '/depot' },
    ...parts.map((part, idx) => ({
      label: part,
      to: `/depot/${parts.slice(0, idx + 1).join('/')}`,
    })),
  ];
  return (
    <nav className="flex items-center gap-1 font-mono text-xs">
      {segments.map((s, i) => {
        const isLast = i === segments.length - 1;
        return (
          <span key={i} className="inline-flex items-center gap-1">
            {i > 0 && <span className="text-muted-foreground">/</span>}
            <Link
              to={s.to}
              className={cn(
                'inline-flex items-center gap-1 hover:underline',
                isLast ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {i === 0 && <IconHome size={12} />}
              {s.label}
            </Link>
          </span>
        );
      })}
    </nav>
  );
}

// ============================================================================
// DirectoryView — the main listing
// ============================================================================

function DirectoryView({ depotPath }: { depotPath: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['depot.list', depotPath],
    queryFn: () => api.depotList(depotPath),
  });

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [depotPath]);
  const selectionApi: SelectionApi = useMemo(() => ({
    selected,
    toggle: (path: string) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(path)) next.delete(path);
        else next.add(path);
        return next;
      }),
    selectMany: (paths: string[]) =>
      setSelected((prev) => {
        const next = new Set(prev);
        for (const p of paths) next.add(p);
        return next;
      }),
    clear: () => setSelected(new Set()),
  }), [selected]);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 250);
    return () => window.clearTimeout(id);
  }, [searchInput]);
  const parsedSearch = useMemo(() => parseSearchInput(debouncedSearch), [debouncedSearch]);
  const searchActive =
    parsedSearch.q.length > 0 ||
    parsedSearch.include.length > 0 ||
    parsedSearch.includeClass.length > 0 ||
    parsedSearch.propQuery.length > 0 ||
    parsedSearch.budget !== null ||
    parsedSearch.brokenRefs;
  const searchQuery = useQuery({
    queryKey: ['depot.search', depotPath, parsedSearch],
    queryFn: () => api.depotSearch(depotPath, parsedSearch.q, {
      include: parsedSearch.include,
      exclude: parsedSearch.exclude,
      includeClass: parsedSearch.includeClass,
      excludeClass: parsedSearch.excludeClass,
      propQuery: parsedSearch.propQuery,
      ...(parsedSearch.budget ? { budget: parsedSearch.budget } : {}),
      ...(parsedSearch.brokenRefs ? { brokenRefs: true } : {}),
    }),
    enabled: searchActive,
  });

  const { data: locksData } = useQuery({
    queryKey: ['depot.locks', depotPath],
    queryFn: () => api.locks(depotPath),
    staleTime: 30_000,
  });

  const lockMap = useMemo(() => {
    const m = new Map<string, LockEntry>();
    for (const l of locksData?.locks ?? []) m.set(l.depotPath, l);
    return m;
  }, [locksData]);

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'list';
    const v = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return v === 'grid' || v === 'list' ? v : 'list';
  });

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    );
  }
  if (error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertTitle>Failed to load directory</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }
  if (!data) return null;

  const empty = data.dirs.length === 0 && data.files.length === 0;
  if (empty) {
    return <p className="py-12 text-center text-sm text-muted-foreground">Empty.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-nowrap items-center justify-between gap-3">
        <div className="relative max-w-[360px] flex-1">
          <IconSearch
            size={12}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder={`Search files under ${basename(depotPath)}/…`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            className="pl-7 pr-7"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="clear search"
              className="absolute right-2 top-1/2 inline-flex h-4 w-4 -translate-y-1/2 items-center justify-center text-muted-foreground hover:text-foreground"
            >
              <IconX size={12} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {data.dirs.length} {data.dirs.length === 1 ? 'folder' : 'folders'},{' '}
            {data.files.length} {data.files.length === 1 ? 'file' : 'files'}
          </span>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      <SelectionContext.Provider value={selectionApi}>
        {depotPath === '//' ? (
          <DepotsOverview depots={data.dirs} />
        ) : (
          <div className="flex flex-nowrap items-start gap-4">
            <FacetSidebar
              depotPath={depotPath}
              activeClasses={new Set(parsedSearch.includeClass)}
              activeTags={new Set(parsedSearch.include.flat())}
              activeBudget={parsedSearch.budget}
              brokenRefsActive={parsedSearch.brokenRefs}
              onToggleClass={(name) =>
                setSearchInput((prev) => toggleSearchToken(prev, 'class', name))
              }
              onToggleTag={(name) =>
                setSearchInput((prev) => toggleSearchToken(prev, 'tag', name))
              }
              onToggleBudget={(level) =>
                setSearchInput((prev) => toggleSearchToken(prev, 'budget', level))
              }
              onToggleBrokenRefs={() =>
                setSearchInput((prev) => toggleSearchToken(prev, 'brokenrefs', '1'))
              }
            />
            <div className="min-w-0 flex-1">
              {searchActive ? (
                <SearchResults
                  query={debouncedSearch}
                  scope={depotPath}
                  state={searchQuery}
                  lockMap={lockMap}
                  viewMode={viewMode}
                />
              ) : viewMode === 'list' ? (
                <DirectoryList depotPath={depotPath} dirs={data.dirs} files={data.files} lockMap={lockMap} />
              ) : (
                <DirectoryGrid dirs={data.dirs} files={data.files} lockMap={lockMap} />
              )}
            </div>
          </div>
        )}
        <SelectionBar />
      </SelectionContext.Provider>
    </div>
  );
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  return (
    <div
      role="group"
      aria-label="View mode"
      className="inline-flex overflow-hidden rounded-sm border border-neutral-800 bg-neutral-950"
    >
      {(['list', 'grid'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cn(
            'px-3 py-1 text-xs transition-colors',
            value === v
              ? 'bg-neutral-800 text-foreground'
              : 'text-muted-foreground hover:bg-neutral-900 hover:text-foreground',
          )}
        >
          {v === 'list' ? 'List' : 'Grid'}
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// DepotsOverview — // root listing
// ============================================================================

type DepotSortKey = 'recent' | 'size' | 'name';
type SortDir = 'asc' | 'desc';
interface DepotSort {
  key: DepotSortKey;
  dir: SortDir;
}

// Natural default direction when a sort key is first selected: newest/
// biggest first for recent/size, A→Z for name.
const DEFAULT_SORT_DIR: Record<DepotSortKey, SortDir> = {
  recent: 'desc',
  size: 'desc',
  name: 'asc',
};

function loadDepotSort(): DepotSort {
  if (typeof window === 'undefined') return { key: 'recent', dir: 'desc' };
  try {
    const raw = window.localStorage.getItem(DEPOT_SORT_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<DepotSort>;
      if (
        (p.key === 'recent' || p.key === 'size' || p.key === 'name') &&
        (p.dir === 'asc' || p.dir === 'desc')
      ) {
        return { key: p.key, dir: p.dir };
      }
    }
  } catch {
    /* fall through to default */
  }
  return { key: 'recent', dir: 'desc' };
}

// Sort comparator. Missing values (no depotInfo / null metric) always sort
// to the bottom regardless of direction, so depots still indexing don't
// jump to the top.
function compareDepots(a: DepotDir, b: DepotDir, sort: DepotSort): number {
  if (sort.key === 'name') {
    const cmp = basename(a.path).localeCompare(basename(b.path), undefined, {
      sensitivity: 'base',
      numeric: true,
    });
    return sort.dir === 'asc' ? cmp : -cmp;
  }
  const pick = (d: DepotDir): number | null =>
    sort.key === 'size'
      ? (d.depotInfo?.fileSize ?? null)
      : (d.depotInfo?.latestChange ?? null);
  const av = pick(a);
  const bv = pick(b);
  if (av === null && bv === null) return 0;
  if (av === null) return 1; // a missing → after b
  if (bv === null) return -1; // b missing → after a
  const cmp = av - bv;
  return sort.dir === 'asc' ? cmp : -cmp;
}

function DepotsOverview({ depots }: { depots: (DepotDir | string)[] }) {
  const [sort, setSort] = useState<DepotSort>(loadDepotSort);
  useEffect(() => {
    window.localStorage.setItem(DEPOT_SORT_STORAGE_KEY, JSON.stringify(sort));
  }, [sort]);

  const sorted = useMemo(() => {
    const norm = depots.map(normDir);
    return norm.sort((a, b) => compareDepots(a, b, sort));
  }, [depots, sort]);

  if (depots.length === 0) {
    return <p className="py-12 text-center text-sm text-muted-foreground">No depots yet.</p>;
  }

  // Clicking the active key flips its direction; clicking another key
  // switches to it at that key's natural default direction.
  const onSort = (key: DepotSortKey): void =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: DEFAULT_SORT_DIR[key] },
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sort</span>
        <div
          role="group"
          aria-label="Sort depots"
          className="inline-flex overflow-hidden rounded-sm border border-neutral-800 bg-neutral-950"
        >
          {(
            [
              ['recent', 'Most recent'],
              ['size', 'Size'],
              ['name', 'Name'],
            ] as const
          ).map(([key, label]) => {
            const active = sort.key === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSort(key)}
                aria-pressed={active}
                className={cn(
                  'px-3 py-1 text-xs transition-colors',
                  active
                    ? 'bg-neutral-800 text-foreground'
                    : 'text-muted-foreground hover:bg-neutral-900 hover:text-foreground',
                )}
              >
                {label}
                {active && (
                  <span className="ml-1 tabular-nums" aria-hidden>
                    {sort.dir === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {sorted.map((d) => (
        <DepotOverviewCard key={d.path} dir={d} />
      ))}
    </div>
  );
}

function DepotOverviewCard({ dir }: { dir: DepotDir }) {
  const navigate = useNavigate();
  const info = dir.depotInfo;
  const depotHref = `/depot${depotPathToUrl(dir.path)}`;

  // Card-level click takes the user to the depot listing, but the
  // inner change / user links navigate to their own destinations.
  // Nesting <Link>s inside an outer <Link> produces invalid HTML
  // (and React 19 warns) — wire the outer affordance via
  // useNavigate() so only the inner anchors are real <a>s.
  const goToDepot = (e: React.MouseEvent): void => {
    // Honor cmd/ctrl/middle-click for "open in new tab" semantics.
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    navigate(depotHref);
  };

  return (
    <Card
      role="link"
      tabIndex={0}
      aria-label={`Open ${basename(dir.path)}`}
      onClick={goToDepot}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(depotHref);
        }
      }}
      className="relative overflow-hidden cursor-pointer transition-colors hover:border-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <DepotActivityBackdrop path={dir.path} />
      <CardContent className="relative z-10 flex flex-nowrap items-start justify-between gap-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <IconFolder size={20} className="text-theia-500" />
            <span className="font-mono text-base font-semibold">
              {basename(dir.path)}
            </span>
            {info && (
              <Badge tone={info.type === 'stream' ? 'orange' : 'neutral'}>
                {info.type}
              </Badge>
            )}
          </div>
          {info?.description && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
              {info.description}
            </p>
          )}
        </div>
        {info && (
          <div className="flex flex-nowrap items-center gap-6">
            <div className="flex flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Files
              </span>
              <span className="text-sm font-medium tabular-nums">
                {info.fileCount !== null ? info.fileCount.toLocaleString() : '—'}
              </span>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Size
              </span>
              <span className="text-sm font-medium tabular-nums">
                {info.fileSize !== null ? formatBytes(info.fileSize) : '—'}
              </span>
            </div>
            <div className="flex min-w-[140px] flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Latest change
              </span>
              {info.latestChange ? (
                <span className="text-sm">
                  <Link
                    to={`/changes/${info.latestChange}`}
                    className="font-medium text-theia-400 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    @{info.latestChange}
                  </Link>
                  {info.latestChangeAt && (
                    <span className="text-xs text-muted-foreground">
                      {' · '}{new Date(info.latestChangeAt).toLocaleDateString()}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
              {info.latestChangeUser && (
                <span className="text-xs text-muted-foreground">
                  by{' '}
                  <Link
                    to={`/users/${info.latestChangeUser}`}
                    className="text-theia-400 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {info.latestChangeUser}
                  </Link>
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// DepotActivityBackdrop — a GitHub-style 53×7 submit heatmap rendered
// *behind* the depot card. Right-aligned so the most-recent week sits at
// the grid's right edge, which is inset to stop just left of the
// Files/Size stat cluster (so recent days are never obscured). A
// left→right opacity ramp (mask) fades it from invisible at the far left
// (under the depot name) to full strength on the right.
const STATS_RESERVE_PX = 320; // approx width of the Files/Size/Latest cluster
// Ease-in exponent for the per-column opacity ramp. opacity = t^RAMP_EXP
// where t is the column's normalized left→right position (0 = oldest, 1 =
// newest). >1 keeps older history faint and lets the curve climb late so
// recent weeks pop — smooth (no gradient-stop banding) and trivially
// re-tunable. ~1.7 matches the hand-tuned mask we replaced.
const RAMP_EXP = 1.7;
const COL_STAGGER_MS = 0; // no sweep — every column starts fading immediately
const COL_BASE_MS = 1300; // base fade-in duration — slow enough that the
// per-column duration spread is actually perceptible (a fast fade hides it).
const COL_DUR_JITTER_MS = 650; // ± random duration spread (≈650–1950ms, a ~3×
// fast-to-slow ratio) so columns visibly animate at different rates.
const COL_DELAY_JITTER_MS = 0; // no start delay — all columns begin at once,
// the duration spread alone desyncs when they finish.

function DepotActivityBackdrop({ path }: { path: string }) {
  const { data } = useQuery({
    queryKey: ['depot.activity', path],
    queryFn: () => api.depotActivity(path),
    staleTime: 5 * 60_000,
  });
  const cells = useMemo(() => buildActivityCells(data?.activity ?? []), [data]);
  // Slice the flat (column-major) cell array into its 7-day columns so each
  // column can carry its own opacity + staggered fade-in.
  const columns = useMemo(() => {
    const out: Array<Array<{ date: string; count: number } | null>> = [];
    for (let c = 0; c < cells.length; c += 7) out.push(cells.slice(c, c + 7));
    return out;
  }, [cells]);
  // Per-column random duration/delay jitter, generated once per column count
  // (not per render) so the desync is stable across re-renders. Each column
  // animates at its own rate so they don't fade in lockstep.
  const jitter = useMemo(
    () =>
      columns.map(() => ({
        durMs: COL_BASE_MS + (Math.random() * 2 - 1) * COL_DUR_JITTER_MS,
        delayJitterMs: (Math.random() * 2 - 1) * COL_DELAY_JITTER_MS,
      })),
    [columns.length],
  );
  if (!data || data.activity.length === 0) return null;
  const lastCol = Math.max(columns.length - 1, 1);
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-y-0 left-0 z-0 flex items-center justify-end overflow-hidden"
      style={{ right: STATS_RESERVE_PX }}
    >
      <div className="flex" style={{ gap: 2, paddingRight: 8 }}>
        {columns.map((col, ci) => {
          // Normalized position 0 (oldest, far left) → 1 (newest, right).
          const t = ci / lastCol;
          const opacity = Math.pow(t, RAMP_EXP);
          const j = jitter[ci] ?? { durMs: COL_BASE_MS, delayJitterMs: 0 };
          return (
            <div
              key={ci}
              className="theia-activity-col flex flex-col"
              style={
                {
                  gap: 2,
                  '--col-opacity': opacity.toFixed(3),
                  // Sweep in oldest→newest as the card loads, with a small
                  // random per-column duration + delay so they desync.
                  animationDuration: `${Math.round(j.durMs)}ms`,
                  animationDelay: `${Math.max(0, Math.round(ci * COL_STAGGER_MS + j.delayJitterMs))}ms`,
                } as CSSProperties
              }
            >
              {col.map((c, ri) => {
                if (!c) return <div key={ri} style={{ width: 10, height: 10 }} />;
                const { bg, glow } = backdropColor(c.count);
                return (
                  <div
                    key={ri}
                    className="rounded-[2px]"
                    style={{ width: 10, height: 10, background: bg, ...(glow ? { boxShadow: `0 0 6px ${bg}` } : {}) }}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 53 weeks × 7 days, Sunday-anchored columns; future days → null. Mirrors
// the user-page ActivityGrid layout.
function buildActivityCells(
  activity: Array<{ date: string; count: number }>,
): Array<{ date: string; count: number } | null> {
  const map = new Map<string, number>();
  for (const a of activity) map.set(a.date, a.count);
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setUTCDate(end.getUTCDate() + (6 - end.getUTCDay()));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (53 * 7 - 1));
  const cells: Array<{ date: string; count: number } | null> = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    const iso = cursor.toISOString().slice(0, 10);
    cells.push(cursor.getTime() > Date.now() ? null : { date: iso, count: map.get(iso) ?? 0 });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return cells;
}

// Theia-orange heatmap tuned for a backdrop: empty days are a faint grid
// lattice; busy days glow.
function backdropColor(n: number): { bg: string; glow: boolean } {
  if (n === 0) return { bg: 'rgba(255,255,255,0.045)', glow: false };
  if (n < 3) return { bg: 'rgba(229,91,43,0.35)', glow: false };
  if (n < 6) return { bg: 'rgba(229,91,43,0.55)', glow: false };
  if (n < 10) return { bg: 'rgba(229,91,43,0.78)', glow: false };
  return { bg: '#e55b2b', glow: true };
}

// ============================================================================
// SelectionBar — sticky bottom-right bulk actions
// ============================================================================

function SelectionBar() {
  const { selected, clear } = useSelection();
  if (selected.size === 0) return null;
  const overCap = selected.size > BULK_MAX_FILES;
  return (
    <div
      role="region"
      aria-label="Selection actions"
      className="fixed bottom-6 right-6 z-50 min-w-[280px] rounded-md border border-neutral-800 bg-card p-3 shadow-lg"
    >
      <div className="flex flex-nowrap items-center justify-between gap-3">
        <span className="text-sm font-medium">
          {selected.size} selected
          {overCap && (
            <span className="ml-1.5 text-xs text-red-400">
              (max {BULK_MAX_FILES})
            </span>
          )}
        </span>
        <div className="flex flex-nowrap items-center gap-1.5">
          <Button variant="ghost" onClick={clear}>Clear</Button>
          <Button
            variant="primary"
            disabled={overCap}
            onClick={() => api.downloadZip(Array.from(selected))}
          >
            <IconDownload />
            Download ZIP
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SearchResults
// ============================================================================

function SearchResults({
  query,
  scope,
  state,
  lockMap,
  viewMode,
}: {
  query: string;
  scope: string;
  state: ReturnType<typeof useQuery<{
    results: import('../api.js').DepotEntry[];
    total: number;
    capped: boolean;
    scanned: number;
  }>>;
  lockMap: Map<string, LockEntry>;
  viewMode: ViewMode;
}) {
  if (state.isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Searching {scope}/…</span>
      </div>
    );
  }
  if (state.error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>{state.error.message}</AlertDescription>
      </Alert>
    );
  }
  const data = state.data;
  if (!data) return null;
  if (data.results.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No matches for <strong>{query}</strong> in this scope.
        {data.capped &&
          ` Only the first ${data.scanned.toLocaleString()} files were scanned — narrow the path and try again.`}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">
        {data.total} match{data.total === 1 ? '' : 'es'} for "{query}"
        {data.results.length < data.total && ` (showing first ${data.results.length})`}
        {data.capped && ` · scope clipped at ${data.scanned} files — narrow the path`}
      </p>
      {viewMode === 'list' ? (
        <div className="rounded-md border border-neutral-800">
          <table className="w-full text-xs">
            <tbody>
              {data.results.map((f) => (
                <SearchRow
                  key={f.depotFile}
                  file={f}
                  lock={lockMap.get(f.depotFile) ?? null}
                  scope={scope}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
          {data.results.map((f) => (
            <SearchGridCard
              key={f.depotFile}
              file={f}
              lock={lockMap.get(f.depotFile) ?? null}
              scope={scope}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SearchRow({
  file,
  lock,
  scope,
}: {
  file: import('../api.js').DepotEntry;
  lock: LockEntry | null;
  scope: string;
}) {
  const { selected, toggle } = useSelection();
  const isSelected = selected.has(file.depotFile);
  const inMultiSelectMode = selected.size > 0;
  const openFile = useFileHref();
  return (
    <tr
      onClick={inMultiSelectMode ? () => toggle(file.depotFile) : undefined}
      className={cn(
        'border-b border-neutral-800/60 transition-colors hover:bg-neutral-900/40',
        isSelected && 'bg-theia-950/40',
        inMultiSelectMode && 'cursor-pointer',
      )}
    >
      <td className="w-8 px-2 py-1.5">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => toggle(file.depotFile)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${basename(file.depotFile)}`}
        />
      </td>
      <td className="w-9 px-2 py-1.5">
        <IconFile size={16} className="text-muted-foreground" />
      </td>
      <td className="px-2 py-1.5 font-mono">
        <Link
          to={openFile(file.depotFile)}
          className="text-theia-400 hover:underline"
          onClick={(e) => {
            if (inMultiSelectMode) e.preventDefault();
          }}
        >
          {file.depotFile.replace(scope, '')}
        </Link>
      </td>
      <td className="w-8 px-2 py-1.5">
        {lock && (
          <LockTooltip lock={lock}>
            <IconLock size={12} className="text-theia-400" />
          </LockTooltip>
        )}
      </td>
      <td className="w-20 px-2 py-1.5">
        <Badge>#{file.rev ?? '?'}</Badge>
      </td>
    </tr>
  );
}

function SearchGridCard({
  file,
  lock,
  scope,
}: {
  file: import('../api.js').DepotEntry;
  lock: LockEntry | null;
  scope: string;
}) {
  const { selected, toggle } = useSelection();
  const isSelected = selected.has(file.depotFile);
  const inMultiSelectMode = selected.size > 0;
  const showHoverPreview = !inMultiSelectMode;
  const openFile = useFileHref();
  const relative = file.depotFile.replace(scope, '');

  const inner = (
    <Link
      to={openFile(file.depotFile)}
      className="block no-underline"
      title={file.depotFile}
      onClick={(e) => {
        if (inMultiSelectMode) {
          e.preventDefault();
          toggle(file.depotFile);
        }
      }}
    >
      <div
        className={cn(
          'relative flex flex-col gap-2 rounded-md p-3',
          isSelected
            ? 'border-2 border-theia-500'
            : 'border border-neutral-800',
        )}
      >
        <div className="absolute left-2 top-2 z-10 rounded-sm bg-black/60 p-0.5">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggle(file.depotFile)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${basename(file.depotFile)}`}
          />
        </div>
        {lock && (
          <div className="absolute right-2 top-2 z-10 inline-flex items-center rounded-sm bg-black/70 px-1.5 py-0.5">
            <LockTooltip lock={lock}>
              <IconLock size={12} className="text-theia-300" />
            </LockTooltip>
          </div>
        )}
        {file.rev !== null ? (
          <ThumbnailCard
            depotPath={file.depotFile}
            headRev={file.rev}
            filename={basename(file.depotFile)}
          />
        ) : (
          <div className="aspect-square w-full rounded-sm bg-neutral-900" />
        )}
        <span
          className="block truncate font-mono text-xs"
          title={relative}
        >
          {basename(file.depotFile)}
        </span>
        <span className="block truncate font-mono text-[11px] text-muted-foreground">
          {dirOf(relative)}
        </span>
      </div>
    </Link>
  );

  if (!showHoverPreview) return inner;
  return (
    <HoverCard openDelay={400} closeDelay={150}>
      <HoverCardTrigger asChild>{inner}</HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-[380px]">
        <FileQuickPreview file={file} lock={lock} />
      </HoverCardContent>
    </HoverCard>
  );
}

// ============================================================================
// DirectoryList — table view
// ============================================================================

function dirOf(path: string): string {
  const slash = path.lastIndexOf('/');
  return slash >= 0 ? path.slice(0, slash) : '';
}

function LockTooltip({
  lock,
  children,
}: {
  lock: LockEntry;
  children: React.ReactNode;
}) {
  return (
    <Tooltip
      label={
        <div className="flex flex-col gap-0.5">
          <span>Locked by <strong>{lock.user ?? '?'}</strong></span>
          {lock.client && (
            <span className="text-muted-foreground">
              workspace {lock.client}
            </span>
          )}
        </div>
      }
    >
      <span className="inline-flex items-center">{children}</span>
    </Tooltip>
  );
}


/**
 * Latest-change bar above the listing — the p4 counterpart of the Lore
 * browser's commit bar. Backed by /api/depot/latest-change (one
 * `p4 changes -m 1` for the path, recursive), so it stays correct in a
 * directory that contains only subdirectories.
 */
function ChangeBar({ depotPath }: { depotPath: string }) {
  const q = useQuery({
    queryKey: ['depot.latestChange', depotPath],
    queryFn: () => api.depotLatestChange(depotPath),
    retry: false,
    staleTime: 60_000,
  });
  const c = q.data;
  if (!c || c.change === null) return null;
  const user = c.user ?? '';
  return (
    <div className="flex items-center gap-2 border-b border-neutral-800 bg-neutral-900/50 px-3 py-2 text-xs">
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-theia-600/20 text-[9px] font-semibold uppercase text-theia-400"
        title={user}
      >
        {user.slice(0, 2) || '—'}
      </span>
      {user && <span className="shrink-0 font-medium text-foreground">{user}</span>}
      <span className="min-w-0 flex-1 truncate text-muted-foreground" title={c.description}>
        {c.description || '(no description)'}
      </span>
      <Link
        to={`/changes/${String(c.change)}`}
        className="shrink-0 font-mono text-[11px] text-muted-foreground hover:text-theia-400 hover:underline"
      >
        @{c.change}
      </Link>
      <span className="shrink-0 text-muted-foreground">{relativeTime(c.time)}</span>
    </div>
  );
}

function DirectoryList({
  depotPath,
  dirs,
  files,
  lockMap,
}: {
  depotPath: string;
  dirs: (DepotDir | string)[];
  files: import('../api.js').DepotEntry[];
  lockMap: Map<string, LockEntry>;
}) {
  // Directories before files, each A-Z — the ordering every code host uses.
  const sortedDirs = [...dirs].sort((a, b) =>
    basename(normDir(a).path).localeCompare(basename(normDir(b).path)),
  );
  const sortedFiles = [...files].sort((a, b) =>
    basename(a.depotFile).localeCompare(basename(b.depotFile)),
  );
  // Parent link, except at the // root.
  const trimmed = depotPath.replace(/\/+$/, '');
  const parent = trimmed.slice(0, trimmed.lastIndexOf('/'));
  const parentUrl = trimmed !== '/' && parent.length > 1 ? `/depot${depotPathToUrl(parent)}` : null;

  return (
    <div className="overflow-hidden rounded-md border border-neutral-800">
      <ChangeBar depotPath={depotPath} />
      <table className="w-full text-xs">
        <tbody>
          {parentUrl && (
            <tr className="border-b border-neutral-800/60 transition-colors hover:bg-neutral-900/40">
              <td colSpan={5} className="px-3 py-1.5">
                <Link to={parentUrl} className="flex items-center gap-2 text-muted-foreground">
                  <IconCornerLeftUp size={16} className="shrink-0" />
                  <span>..</span>
                </Link>
              </td>
            </tr>
          )}
          {sortedDirs.map((raw) => {
            const d = normDir(raw);
            return (
              <tr
                key={d.path}
                className="border-b border-neutral-800/60 transition-colors hover:bg-neutral-900/40"
              >
                <td className="w-8 px-2 py-1.5" />
                <td className="px-2 py-1.5">
                  <Link
                    to={`/depot${depotPathToUrl(d.path)}`}
                    className="flex items-center gap-2 hover:text-theia-400 hover:underline"
                  >
                    {d.stream ? (
                      <IconGitBranch size={16} className="shrink-0 text-theia-500" />
                    ) : (
                      <IconFolder size={16} className="shrink-0 fill-theia-500/20 text-theia-500" />
                    )}
                    <span>{basename(d.path)}</span>
                  </Link>
                  {d.stream && (
                    <Badge
                      tone="orange"
                      className="ml-2"
                      title={d.stream.parent ? `parent: ${d.stream.parent}` : 'mainline stream'}
                    >
                      stream · {d.stream.type}
                    </Badge>
                  )}
                </td>
                <td className="w-8 px-2 py-1.5" />
                <td className="w-24 px-2 py-1.5" />
                <td className="w-28 px-2 py-1.5" />
              </tr>
            );
          })}
          {sortedFiles.map((f) => (
            <FileRow
              key={f.depotFile}
              file={f}
              lock={lockMap.get(f.depotFile) ?? null}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FileRow({
  file,
  lock,
}: {
  file: import('../api.js').DepotEntry;
  lock: LockEntry | null;
}) {
  const { selected, toggle } = useSelection();
  const isSelected = selected.has(file.depotFile);
  const inMultiSelectMode = selected.size > 0;
  const openFile = useFileHref();
  return (
    <tr
      onClick={inMultiSelectMode ? () => toggle(file.depotFile) : undefined}
      className={cn(
        'border-b border-neutral-800/60 transition-colors hover:bg-neutral-900/40',
        isSelected && 'bg-theia-950/40',
        inMultiSelectMode && 'cursor-pointer',
      )}
    >
      <td className="w-8 px-2 py-1.5">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => toggle(file.depotFile)}
          aria-label={`Select ${basename(file.depotFile)}`}
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td className="px-2 py-1.5">
        <span className="flex items-center gap-2">
          <IconFile size={16} className="shrink-0 text-muted-foreground" />
          <Link
            to={openFile(file.depotFile)}
            className="hover:text-theia-400 hover:underline"
            onClick={(e) => {
              if (inMultiSelectMode) e.preventDefault();
            }}
          >
            {basename(file.depotFile)}
          </Link>
          {file.type && (
            <span className="font-mono text-[10px] text-muted-foreground/70">{file.type}</span>
          )}
        </span>
      </td>
      <td className="w-8 px-2 py-1.5">
        {lock && (
          <LockTooltip lock={lock}>
            <IconLock size={12} className="text-theia-400" />
          </LockTooltip>
        )}
      </td>
      {/* Per-row last change — p4 gives this in the listing, unlike Lore. */}
      <td className="w-24 px-2 py-1.5 text-right">
        {file.change !== null ? (
          <Link
            to={`/changes/${String(file.change)}`}
            className="font-mono text-[11px] text-muted-foreground hover:text-theia-400 hover:underline"
            onClick={(e) => {
              if (inMultiSelectMode) e.preventDefault();
            }}
          >
            @{file.change}
          </Link>
        ) : (
          <Badge>#{file.rev ?? '?'}</Badge>
        )}
      </td>
      <td className="w-28 px-2 py-1.5 text-right text-[11px] text-muted-foreground">
        {relativeTime(file.time)}
      </td>
    </tr>
  );
}

// ============================================================================
// DirectoryGrid — grid view
// ============================================================================

function DirectoryGrid({
  dirs,
  files,
  lockMap,
}: {
  dirs: (DepotDir | string)[];
  files: import('../api.js').DepotEntry[];
  lockMap: Map<string, LockEntry>;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
      {dirs.map((raw) => {
        const d = normDir(raw);
        return (
          <Link
            key={d.path}
            to={`/depot${depotPathToUrl(d.path)}`}
            className="block no-underline"
          >
            <div
              className={cn(
                'flex flex-col gap-2 rounded-md p-3',
                d.stream
                  ? 'border border-theia-800'
                  : 'border border-neutral-800',
              )}
            >
              <div className="relative flex aspect-square w-full items-center justify-center rounded-sm bg-neutral-900">
                {d.stream ? (
                  <IconGitBranch size={48} stroke={1.4} className="text-theia-500" />
                ) : (
                  <IconFolder size={48} stroke={1.4} className="text-theia-500" />
                )}
                {d.stream && (
                  <Badge
                    tone="orange"
                    variant="solid"
                    className="absolute right-1.5 top-1.5"
                  >
                    {d.stream.type}
                  </Badge>
                )}
              </div>
              <span
                className="block truncate font-mono text-xs"
                title={d.stream ? `${basename(d.path)} (stream)` : basename(d.path)}
              >
                {basename(d.path)}{d.stream ? '' : '/'}
              </span>
            </div>
          </Link>
        );
      })}
      {files.map((f) => {
        const lock = lockMap.get(f.depotFile);
        return <FileGridCard key={f.depotFile} file={f} lock={lock ?? null} />;
      })}
    </div>
  );
}

function FileQuickPreview({
  file,
  lock,
}: {
  file: import('../api.js').DepotEntry;
  lock: LockEntry | null;
}) {
  const filename = basename(file.depotFile);
  return (
    <div className="flex w-[360px] flex-col gap-2">
      {file.rev !== null ? (
        <ThumbnailCard
          depotPath={file.depotFile}
          headRev={file.rev}
          filename={filename}
        />
      ) : (
        <div className="aspect-square w-full rounded-sm bg-neutral-900" />
      )}
      <div className="flex flex-col gap-0.5">
        <span
          className="font-mono text-sm font-semibold"
          title={file.depotFile}
        >
          {filename}
        </span>
        <div className="flex flex-wrap items-center gap-1.5">
          {file.rev !== null && <Badge>#{file.rev}</Badge>}
          {file.type && <Badge>{file.type}</Badge>}
          {lock && (
            <Badge tone="orange" className="gap-1">
              <IconLock size={10} />
              Locked by {lock.user ?? '?'}
            </Badge>
          )}
        </div>
        {file.change && (
          <span className="text-xs text-muted-foreground">
            Last @{file.change}
            {file.time && ` · ${new Date(file.time).toLocaleDateString()}`}
          </span>
        )}
        <span className="mt-1 text-[11px] text-muted-foreground">
          Click to open · hover the card to keep this preview
        </span>
      </div>
    </div>
  );
}

function FileGridCard({
  file,
  lock,
}: {
  file: import('../api.js').DepotEntry;
  lock: LockEntry | null;
}) {
  const { selected, toggle } = useSelection();
  const isSelected = selected.has(file.depotFile);
  const inMultiSelectMode = selected.size > 0;
  const showHoverPreview = !inMultiSelectMode;
  const openFile = useFileHref();

  const inner = (
    <Link
      to={openFile(file.depotFile)}
      className="block no-underline"
      onClick={(e) => {
        if (inMultiSelectMode) {
          e.preventDefault();
          toggle(file.depotFile);
        }
      }}
    >
      <div
        className={cn(
          'relative flex flex-col gap-2 rounded-md p-3',
          isSelected
            ? 'border-2 border-theia-500'
            : 'border border-neutral-800',
        )}
      >
        <div className="absolute left-2 top-2 z-10 rounded-sm bg-black/60 p-0.5">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggle(file.depotFile)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${basename(file.depotFile)}`}
          />
        </div>
        {lock && (
          <div className="absolute right-2 top-2 z-10 inline-flex items-center rounded-sm bg-black/70 px-1.5 py-0.5">
            <LockTooltip lock={lock}>
              <IconLock size={12} className="text-theia-300" />
            </LockTooltip>
          </div>
        )}
        {file.rev !== null ? (
          <ThumbnailCard
            depotPath={file.depotFile}
            headRev={file.rev}
            filename={basename(file.depotFile)}
          />
        ) : (
          <div className="aspect-square w-full rounded-sm bg-neutral-900" />
        )}
        <div className="flex flex-nowrap items-center justify-between gap-1">
          <span
            className="min-w-0 flex-1 truncate font-mono text-xs"
            title={basename(file.depotFile)}
          >
            {basename(file.depotFile)}
          </span>
          {file.rev !== null && <Badge>#{file.rev}</Badge>}
        </div>
      </div>
    </Link>
  );

  if (!showHoverPreview) return inner;
  return (
    <HoverCard openDelay={400} closeDelay={150}>
      <HoverCardTrigger asChild>{inner}</HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-[380px]">
        <FileQuickPreview file={file} lock={lock} />
      </HoverCardContent>
    </HoverCard>
  );
}

// ============================================================================
// FileView — file detail
// ============================================================================

function FileView({ depotPath }: { depotPath: string }) {
  const fstat = useQuery({
    queryKey: ['depot.fstat', depotPath],
    queryFn: () => api.fstat(depotPath),
  });

  const headChange = fstat.data?.headChange ?? null;
  const headChangeDetail = useQuery({
    queryKey: ['changes.detail', headChange],
    queryFn: () => api.changeDetail(headChange!),
    enabled: headChange !== null,
  });

  const locksQuery = useQuery({
    queryKey: ['depot.locks.file', depotPath],
    queryFn: () => api.locks(depotPath),
    staleTime: 30_000,
  });
  const fileLock = locksQuery.data?.locks[0] ?? null;

  // Only `.uasset` files get classified by the renderer (see
  // pickWorker in renderer/src/worker.ts). Pinging /api/depot/class
  // for `.umap`, `.uefnproject`, etc. always 404s — no point
  // surfacing those as red-flagged network failures in DevTools.
  const fileExtForClass = extOf(depotPath);
  const classifiable = fileExtForClass === 'uasset';
  const classQuery = useQuery({
    queryKey: ['depot.class', depotPath],
    queryFn: () => api.classForPath(depotPath),
    enabled: classifiable,
    retry: false,
  });
  const assetClass = classQuery.data?.assetClass ?? null;

  const isText = fstat.data?.headType?.startsWith('text') ?? false;
  const isUTF = fstat.data?.headType?.startsWith('utf') ?? false;
  const looksRenderableAsText = isText || isUTF;
  const fileExt = extOf(depotPath);
  const isDamPreviewable = DAM_PREVIEWABLE_EXTS.has(fileExt);
  const isUasset = fileExt === 'uasset';
  const openFile = useFileHref();

  const [activeTab, setActiveTab] = useState<'content' | 'history' | 'references'>('content');
  const [compareRev, setCompareRev] = useState<number | null>(null);

  const content = useQuery({
    queryKey: ['depot.file', depotPath],
    queryFn: async () => {
      const res = await fetch(api.fileUrl(depotPath), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    },
    enabled: looksRenderableAsText && !isDamPreviewable && !!fstat.data,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  if (fstat.isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Loading file metadata…</span>
      </div>
    );
  }
  if (fstat.error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertTitle>Failed to load file</AlertTitle>
        <AlertDescription>{fstat.error.message}</AlertDescription>
      </Alert>
    );
  }
  if (!fstat.data) return null;

  const headRev = fstat.data.headRev;
  const filename = basename(fstat.data.depotFile);

  const contentBody = compareRev !== null && headRev !== null ? (
    <RevisionDiff
      depotPath={fstat.data.depotFile}
      headRev={headRev}
      compareRev={compareRev}
      filename={filename}
      fileSize={fstat.data.fileSize}
      isText={looksRenderableAsText}
      isDamPreviewable={isDamPreviewable}
      onClose={() => setCompareRev(null)}
    />
  ) : isDamPreviewable && headRev !== null ? (
    <AssetPreview
      depotPath={fstat.data.depotFile}
      headRev={headRev}
      filename={filename}
      fileSize={fstat.data.fileSize}
    />
  ) : looksRenderableAsText ? (
    content.isLoading ? (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Loading content…</span>
      </div>
    ) : content.error ? (
      <Alert tone="destructive">
        <AlertDescription>{content.error.message}</AlertDescription>
      </Alert>
    ) : (
      <Suspense fallback={<Spinner />}>
        <CodeView
          filename={filename}
          content={content.data ?? ''}
          fileSize={fstat.data.fileSize}
          rawUrl={api.fileUrl(depotPath)}
        />
      </Suspense>
    )
  ) : (
    <Card>
      <CardContent className="py-4">
        <p className="text-sm text-muted-foreground">
          Binary file ({fstat.data.headType}). Inline preview not supported yet.{' '}
          <a
            href={api.fileUrl(depotPath)}
            download
            className="text-theia-400 hover:underline"
          >
            Download
          </a>
        </p>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-1.5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-mono text-base font-semibold">{filename}</h2>
            <Badge>#{headRev ?? '?'}</Badge>
            <Badge>{fstat.data.headType ?? '?'}</Badge>
            {assetClass && (
              <Tooltip label="UE asset class (detected at render time)">
                <span>
                  <Badge tone="orange">{assetClass}</Badge>
                </span>
              </Tooltip>
            )}
            {isUasset && headRev !== null && (
              <AssetBudgetBadge
                depotPath={fstat.data.depotFile}
                rev={headRev}
                assetClass={assetClass}
              />
            )}
            {fstat.data.fileSize !== null && (
              <Badge>{formatBytes(fstat.data.fileSize)}</Badge>
            )}
            {fileLock && (
              <LockTooltip lock={fileLock}>
                <Badge tone="orange" className="gap-1">
                  <IconLock size={11} />
                  Locked by {fileLock.user ?? '?'}
                </Badge>
              </LockTooltip>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Last change{' '}
            {fstat.data.headChange ? (
              <Link
                to={`/changes/${fstat.data.headChange}`}
                className="text-theia-400 hover:underline"
              >
                @{fstat.data.headChange}
              </Link>
            ) : (
              '@?'
            )}
            {headChangeDetail.data?.user ? (
              <>
                {' '}by{' '}
                <Link
                  to={`/users/${headChangeDetail.data.user}`}
                  className="text-theia-400 hover:underline"
                >
                  {headChangeDetail.data.user}
                </Link>
              </>
            ) : null}
            {' '}·{' '}
            {fstat.data.headTime ? new Date(fstat.data.headTime).toLocaleString() : '—'}
          </p>
          <TagChips depotPath={fstat.data.depotFile} />
        </CardContent>
      </Card>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'content' | 'history' | 'references')}
      >
        <TabsList>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="history">
            <IconClock size={12} />
            History
          </TabsTrigger>
          {isUasset && <TabsTrigger value="references">References</TabsTrigger>}
        </TabsList>

        <TabsContent value="content">{contentBody}</TabsContent>

        <TabsContent value="history">
          <HistoryPanel
            depotPath={fstat.data.depotFile}
            headRev={headRev}
            canCompare={isDamPreviewable || looksRenderableAsText}
            onCompare={(rev) => {
              setCompareRev(rev);
              setActiveTab('content');
            }}
          />
        </TabsContent>

        {isUasset && headRev !== null && (
          <TabsContent value="references">
            <DependencyPanel depotPath={fstat.data.depotFile} rev={headRev} hrefFor={openFile} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function HistoryPanel({
  depotPath,
  headRev,
  canCompare,
  onCompare,
}: {
  depotPath: string;
  headRev: number | null;
  canCompare: boolean;
  onCompare: (rev: number) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['depot.history', depotPath],
    queryFn: () => api.history(depotPath, 50),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Loading history…</span>
      </div>
    );
  }
  if (error) {
    return (
      <Alert tone="destructive">
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }
  if (!data || data.entries.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        No history recorded.
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {data.entries.map((e, idx) => (
        <HistoryRow
          key={e.rev}
          entry={e}
          isHead={e.rev === headRev}
          isFirst={idx === 0}
          canCompare={canCompare && e.rev !== headRev}
          onCompare={() => onCompare(e.rev)}
        />
      ))}
    </div>
  );
}

function HistoryRow({
  entry,
  isHead,
  isFirst,
  canCompare,
  onCompare,
}: {
  entry: HistoryEntry;
  isHead: boolean;
  isFirst: boolean;
  canCompare: boolean;
  onCompare: () => void;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-4 px-1 py-3',
        !isFirst && 'border-t border-neutral-800',
      )}
    >
      <div className="min-w-[72px]">
        <Badge>#{entry.rev}</Badge>
        {isHead && (
          <Badge tone="green" variant="solid" className="ml-1 mt-1">
            HEAD
          </Badge>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-nowrap items-baseline gap-2">
          <span className="font-mono text-sm">{entry.user ?? '?'}</span>
          <span className="text-xs text-muted-foreground">
            @{entry.change ?? '?'} ·{' '}
            {entry.time ? new Date(entry.time).toLocaleString() : '—'}
          </span>
          {entry.action && <Badge variant="outline">{entry.action}</Badge>}
        </div>
        {entry.description && (
          <p className="mt-1 whitespace-pre-wrap break-words text-xs text-muted-foreground">
            {entry.description}
          </p>
        )}
      </div>
      {canCompare && (
        <button
          type="button"
          onClick={onCompare}
          className="shrink-0 text-xs text-theia-400 hover:underline"
        >
          Compare with HEAD
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function depotPathToUrl(depotPath: string): string {
  return depotPath.replace(/^\/\//, '/');
}

function normDir(d: string | DepotDir): DepotDir {
  if (typeof d === 'string') return { path: d, stream: null };
  return d;
}

